/** @jsxImportSource @emotion/react */
import { SerializedStyles } from '@emotion/css/macro'
import { useTypes } from '@sportninja/common/hooks/useTypes'
import React, { useEffect, useMemo } from 'react'
import { SelectInput } from '../SelectInput'

/**
 * PlayerPositionSelect component for selecting player positions based on sport
 * @component
 */
type PlayerPositionSelectProps = {
  /** Current selected position id */
  value: number | undefined
  /** Callback when selection changes */
  onChange: (value: number | undefined) => void
  /** Selected sport id – drives position options */
  sportId?: string
  /** Field name for error handling and identification */
  name?: string
  /** Object containing field validation errors */
  fieldErrors?: any
  /** Custom error messages */
  customError?: any
  /** Label text displayed above the select */
  label?: string
  /** Whether the select is disabled */
  disabled?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Whether to allow clearing the selection */
  allowClear?: boolean
  /** Whether to show search functionality */
  showSearch?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Custom CSS styles using emotion css */
  style?: SerializedStyles
  /** Custom filter function for search */
  filterOption?: (input: string, option: any) => boolean
  /** Callback when an option is selected */
  onSelect?: (value: number) => void
  /** Custom content to show when no options are found */
  notFoundContent?: React.ReactNode
}

const defaultFilterOption = (input: string, option: any) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

export const PlayerPositionSelect: React.FC<PlayerPositionSelectProps> = ({
  value,
  onChange,
  sportId,
  name = 'playerPosition',
  fieldErrors = {},
  customError = {},
  label = 'Player Position *',
  disabled = false,
  placeholder,
  allowClear = false,
  showSearch = true,
  tabIndex,
  style,
  filterOption = defaultFilterOption,
  onSelect,
  notFoundContent,
}) => {
  console.log('🔄 PlayerPositionSelect: initializing component', {
    sportId,
    value,
  })

  const { types, fetchTypes } = useTypes()

  // Fetch types on mount if not already loaded
  useEffect(() => {
    if (!types || types?.length === 0) {
      console.log('🔄 PlayerPositionSelect: fetching types')
      fetchTypes()
    }
  }, [fetchTypes, types])

  // Memoize positions based on sport ID and types
  const positions = useMemo(() => {
    console.log('🔄 PlayerPositionSelect: computing positions', {
      sportId,
      typesLength: types?.length,
    })

    if (!sportId || !types || types.length === 0) {
      return []
    }

    // First, find the type for the sport id
    const sportType = types.find((type) => type.sport_id === sportId.toString())

    // Return empty if no sport type is found
    if (!sportType) {
      console.log(
        '🔄 PlayerPositionSelect: no sport type found for sportId',
        sportId
      )
      return []
    }

    const playerTypes = sportType?.data?.find(
      (type) => type.player_types !== undefined
    )

    const positions = playerTypes?.player_types || []
    console.log('🔄 PlayerPositionSelect: found positions', positions.length)

    return positions
  }, [types, sportId])

  // Build options array
  const options = useMemo(() => {
    return positions.map((position) => ({
      label: position.name_full,
      value: position.id.toString(), // Convert to string to match SelectInput expectations
    }))
  }, [positions])

  // Determine if component should be disabled
  const isDisabled = disabled || !sportId || options.length === 0

  // Determine placeholder text
  const placeholderText = useMemo(() => {
    if (placeholder) {
      return placeholder
    }
    if (!sportId) {
      return 'Select a sport first'
    }
    if (options.length === 0) {
      return 'No positions available'
    }
    return 'Select a position'
  }, [placeholder, sportId, options.length])

  // Handle onChange with proper type conversion
  const handleChange = (newValue: string | undefined) => {
    if (newValue === undefined) {
      onChange(undefined)
    } else {
      onChange(Number(newValue))
    }
  }

  // Handle onSelect with proper type conversion
  const handleSelect = (selectedValue: string) => {
    if (onSelect) {
      onSelect(Number(selectedValue))
    }
  }

  console.log('🔄 PlayerPositionSelect: rendering', {
    optionsCount: options.length,
    isDisabled,
    placeholderText,
  })

  return (
    <SelectInput
      name={name}
      label={label}
      value={value?.toString() || ''}
      onChange={handleChange}
      options={options}
      disabled={isDisabled}
      placeholder={placeholderText}
      fieldErrors={fieldErrors}
      customError={customError}
      showSearch={showSearch}
      filterOption={filterOption}
      allowClear={allowClear}
      tabIndex={tabIndex}
      onSelect={handleSelect}
      notFoundContent={notFoundContent}
      style={style}
    />
  )
}

export default PlayerPositionSelect
